import { useEffect, useState } from 'react';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useAppSelector } from 'src/app/hooks';
import { useForm, Controller } from 'src/common/form';
import { useGetRoomNpsByUserQuery, useSubmitRoomNpsMutation } from 'src/features/room/room.slice';
import { useTrackEventMutation } from 'src/common/slices';
import { RoomNps } from 'src/common/interfaces/room-nps';
import { StyledCommentArea, StyledHiddenRadio, StyledRadioGroup, StyledRadioItem, StyledScoreContainer, StyledScoreLabel, StyledScoreScale, StyledRoomFeedbackContainer, StyledSubmitButton, StyledTitle } from './room-nps-form.styles';
const scoreOptions = Array.from({
  length: 10
}, (_, i) => ({
  label: (i + 1).toString(),
  value: (i + 1).toString(),
  name: 'score'
}));
const scoreLabels = [{
  label: '👎 Not likely at all',
  min: 1,
  max: 6
}, {
  label: 'Very likely',
  min: 7,
  max: 8
}, {
  label: 'Extremely likely 👍',
  min: 9,
  max: 10
}];
const getScoreTitle = (score: number | null) => {
  if (!score) return 'How likely are you to recommend this room to others?';
  if (score <= 6) return 'We appreciate your feedback! What can we improve to enhance your experience?';
  if (score <= 8) return 'Thanks for your feedback! What would make your experience even better?';
  return "Glad you're enjoying it! What did you love the most?";
};
export interface RoomNpsProps {
  roomCode: string;
  roomProgress: number;
}
export const RoomNpsForm = ({
  roomCode,
  roomProgress
}: RoomNpsProps) => {
  const {
    tutorPanelOpen,
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const [currentScore, setCurrentScore] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isClosed, setIsClosed] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    watch,
    setValue
  } = useForm<RoomNps>({
    defaultValues: {
      score: 0,
      roomProgress,
      feedback: ''
    }
  });
  const selectedScore = watch('score');
  useEffect(() => {
    setValue('roomProgress', roomProgress);
  }, [roomProgress, setValue]);
  const {
    data: npsData,
    isLoading
  } = useGetRoomNpsByUserQuery(roomCode);
  const [submitRoomNps, mutationResult] = useSubmitRoomNpsMutation();
  const [trackEvent] = useTrackEventMutation();
  const onSubmit = async (data: RoomNps) => {
    await submitRoomNps({
      roomCode,
      ...data
    }).unwrap().then(() => {
      setIsSubmitted(true);
      // eslint-disable-next-line no-void
      void trackEvent({
        event: 'room-nps-form-submitted',
        properties: {
          room_code: roomCode,
          room_progress: roomProgress,
          score: data.score
        }
      });
    }).catch(() => {
      NotificationDispatch('error', 'An error has occurred. Please try again later.');
      setIsSubmitted(false);
    });
  };
  const onClose = () => {
    setIsClosed(true);
  };
  if (isLoading || !!npsData && !isSubmitted || isClosed) {
    return null;
  }
  return <StyledRoomFeedbackContainer $isSplitScreenOpen={tutorPanelOpen || vmPanelOpen} data-sentry-element="StyledRoomFeedbackContainer" data-sentry-component="RoomNpsForm" data-sentry-source-file="room-nps-form.tsx">
      {isSubmitted ? <div data-type="room-nps-form-thank-you">
          <StyledTitle> Thank you! 🎉</StyledTitle>
          <p>Your feedback helps us improve our content! We appreciate the time you took to share your thoughts!</p>
          <StyledSubmitButton type="button" onClick={onClose}>
            <FontAwesomeIcon icon={faCheck} /> Close
          </StyledSubmitButton>
        </div> : <>
          <StyledTitle data-testid="room-nps-form-title">{getScoreTitle(currentScore)}</StyledTitle>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledScoreContainer>
              <Controller name="score" control={control} rules={{
            required: true
          }} render={({
            field
          }) => <StyledRadioGroup>
                    {scoreOptions.map(option => <StyledRadioItem key={'room-nps-'.concat(option.value)} $isSelected={Number(option.value) <= Number(currentScore)}>
                        <StyledHiddenRadio value={option.value} checked={field.value === Number(option.value)} onChange={e => {
                setCurrentScore(Number(e.target.value));
                return field.onChange(Number(e.target.value));
              }} />
                        {option.label}
                      </StyledRadioItem>)}
                  </StyledRadioGroup>} />

              {!!selectedScore && <StyledScoreScale $isSplitScreenOpen={tutorPanelOpen || vmPanelOpen}>
                  {scoreLabels.map(labelItem => {
              const isSelected = !!(currentScore && currentScore >= labelItem.min && currentScore <= labelItem.max);
              return <StyledScoreLabel key={labelItem.label} $isSelected={isSelected}>
                        {labelItem.label}
                      </StyledScoreLabel>;
            })}
                </StyledScoreScale>}
            </StyledScoreContainer>

            <Controller name="feedback" control={control} render={({
          field
        }) => <StyledCommentArea visible={!!selectedScore} placeholder="Add a comment (optional)" maxLength={400} {...field} />} />

            <StyledSubmitButton type="submit" disabled={!selectedScore || mutationResult.isLoading}>
              Submit now
            </StyledSubmitButton>
          </form>
        </>}
    </StyledRoomFeedbackContainer>;
};